import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../logo.png';
import WhatsAppBanner from './WhatsAppBanner';
import { WhatsAppIcon, InstagramIcon, ServerIcon } from './Icons';
import { AvatarWithFallback } from './Avatar';

const API_URL = process.env.REACT_APP_API_URL || '';

const WHATSAPP_SEEN_KEY = 'whatsappBannerSeen_v2';
const INSTAGRAM_URL = 'https://www.instagram.com/beach.lan/';

export default function Layout({ children }) {
  const { user, logout, isAnyAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [lanAvailable, setLanAvailable] = useState(false);

  useEffect(() => {
    if (user && !localStorage.getItem(WHATSAPP_SEEN_KEY)) {
      setWaOpen(true);
    }
  }, [user]);

  useEffect(() => {
    // /api/lan-check prüft serverseitig (app/routers/lan.py auf courtcraft) anhand
    // der öffentlichen Client-IP, ob der Nutzer am LAN-Party-Standort ist (dessen
    // Glasfaser-Anschluss-IP in LAN_PARTY_ALLOWED_IPS eingetragen ist). Der Link
    // zum Dashboard selbst geht direkt an 10.0.0.2 im Party-LAN, NICHT über
    // courtcraft (das steht nur noch im Internet, nicht mehr im selben Netz).
    let cancelled = false;
    fetch('/api/lan-check', { signal: AbortSignal.timeout(2500) })
      .then((r) => r.json())
      .then((data) => { if (!cancelled && data.lan) setLanAvailable(true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const closeWa = () => {
    localStorage.setItem(WHATSAPP_SEEN_KEY, '1');
    setWaOpen(false);
  };

  const handleLogout = () => { logout(); navigate('/'); setMenuOpen(false); };

  const navLink = (to, label, end = false) => {
    const active = end ? location.pathname === to : location.pathname.startsWith(to);
    return (
      <Link to={to} onClick={() => setMenuOpen(false)}
        className={`text-sm font-semibold px-3 py-1.5 rounded transition-all duration-100 no-underline block md:inline-block drop-shadow ${
          active
            ? 'text-white bg-white/15 border border-white/20'
            : 'text-white/80 hover:text-white hover:bg-white/10 border border-transparent'
        }`}>
        {label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <WhatsAppBanner open={waOpen} onClose={closeWa} />

      <header
        className="sticky top-0 z-50 relative overflow-hidden"
        style={{ backgroundImage: "url('/header-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center 30%' }}
      >
        <div className="absolute inset-0 bg-ocean-950/75" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center gap-4 h-16 sm:h-20 relative z-10">
          <Link to="/" className="flex items-center no-underline flex-shrink-0" onClick={() => setMenuOpen(false)}>
            <img src={logo} alt="BeachLAN Hörup" className="h-12 sm:h-16 w-auto object-contain" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-0 h-full">
            {navLink('/info', 'Info')}
            {navLink('/archiv', 'Archiv')}
            {navLink('/galerie', 'Galerie')}
            {user && navLink('/my-tickets', 'Tickets')}
            {user && navLink('/my-tournaments', 'Meine Turniere')}
            {user && navLink('/food-runs', 'Food for Friends')}
            {isAnyAdmin && navLink('/admin', 'Admin')}
            {lanAvailable && (
              <a href="http://10.0.0.2:3000/" target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded transition-all duration-100 no-underline text-white/80 hover:text-white hover:bg-white/10 border border-transparent drop-shadow">
                <ServerIcon className="w-4 h-4" /> LanCache
              </a>
            )}
          </nav>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-3 ml-auto">
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
              className="flex items-center justify-center w-9 h-9 cursor-pointer shadow-md transition-transform hover:scale-105">
              <InstagramIcon className="w-9 h-9" />
            </a>
            {user ? (
              <>
                <button onClick={() => setWaOpen(true)} aria-label="WhatsApp-Gruppe"
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white border-0 cursor-pointer shadow-md transition-colors">
                  <WhatsAppIcon className="w-5 h-5" />
                </button>
                <Link to="/profile"
                  className="flex items-center gap-2 text-xs font-semibold text-white border border-white/30 bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded no-underline transition-all drop-shadow">
                  {user.avatar_url ? (
                    <img
                      src={`${API_URL}${user.avatar_url}`}
                      alt={user.username}
                      className="w-6 h-6 rounded-full object-cover border border-white/30"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-ocean-300/40 flex items-center justify-center text-[10px] font-bold text-white">
                      {user.firstname?.[0]}{user.lastname?.[0]}
                    </div>
                  )}
                  {user.username}
                </Link>
                <button onClick={handleLogout}
                  className="text-sm text-white/70 hover:text-white transition-colors border-0 bg-transparent cursor-pointer font-medium drop-shadow">
                  Abmelden
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm font-semibold text-white border border-white/30 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded no-underline transition-all drop-shadow">Anmelden</Link>
                <Link to="/register" className="btn-sand btn-sm no-underline">Registrieren</Link>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden ml-auto p-2.5 text-white/80 hover:text-white bg-transparent border-0 cursor-pointer drop-shadow"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menü"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {menuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-ocean-900/95 border-t border-ocean-700 px-4 py-2 relative z-10">
            {navLink('/info', 'Info')}
            {navLink('/archiv', 'Archiv')}
            {navLink('/galerie', 'Galerie')}
            {user && navLink('/my-tickets', 'Tickets')}
            {user && navLink('/my-tournaments', 'Meine Turniere')}
            {user && navLink('/food-runs', 'Food for Friends')}
            {isAnyAdmin && navLink('/admin', 'Admin')}
            {lanAvailable && (
              <a href="http://10.0.0.2:3000/" target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded transition-all duration-100 no-underline text-white/80 hover:text-white hover:bg-white/10 border border-transparent drop-shadow block">
                <ServerIcon className="w-4 h-4" /> LanCache
              </a>
            )}
            <div className="border-t border-ocean-700 mt-2 pt-2">
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 w-full text-left text-sm text-ocean-200 hover:text-white px-3 py-2 no-underline">
                <InstagramIcon className="w-4 h-4" /> Instagram
              </a>
              {user ? (
                <>
                  <button onClick={() => { setWaOpen(true); setMenuOpen(false); }}
                    className="flex items-center gap-2 w-full text-left text-sm text-ocean-200 hover:text-white px-3 py-2 bg-transparent border-0 cursor-pointer">
                    <WhatsAppIcon className="w-4 h-4" /> WhatsApp-Gruppe
                  </button>
                  <Link to="/profile" onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 text-sm text-ocean-200 hover:text-white px-3 py-2 no-underline">
                    {user.avatar_url ? (
                      <img
                        src={`${API_URL}${user.avatar_url}`}
                        alt={user.username}
                        className="w-7 h-7 rounded-full object-cover border border-ocean-600"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-ocean-700 flex items-center justify-center text-xs font-bold text-ocean-200">
                        {user.firstname?.[0]}{user.lastname?.[0]}
                      </div>
                    )}
                    Profil ({user.username})
                  </Link>
                  <button onClick={handleLogout}
                    className="block w-full text-left text-sm text-red-400 hover:text-red-300 px-3 py-2 bg-transparent border-0 cursor-pointer">
                    Abmelden
                  </button>
                </>
              ) : (
                <div className="flex gap-2 px-3 py-2">
                  <Link to="/login" onClick={() => setMenuOpen(false)} className="btn-ghost btn-sm no-underline">Anmelden</Link>
                  <Link to="/register" onClick={() => setMenuOpen(false)} className="btn-sand btn-sm no-underline">Registrieren</Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-8 w-full">
        {children}
      </main>

      <footer className="bg-ocean-900 text-ocean-400 border-t border-ocean-800 mt-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-2 text-xs">
          <span>© 2025 BeachLAN Hörup</span>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-ocean-400 hover:text-white no-underline transition-colors">
            <InstagramIcon className="w-4 h-4" /> @beach.lan
          </a>
          <span>Gaming · Community · Sommer</span>
        </div>
      </footer>
    </div>
  );
}
